import React, { useState, useEffect, useMemo } from "react";
import { getSectorEmoji } from "../../utils/stockCardUtils";

// Helper function to convert text to sentence case
const toSentenceCase = (text) => {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

const EventCardView = ({ event, styleCardSize, isNew = false }) => {
  const [showNewAnimation, setShowNewAnimation] = useState(isNew);
  
  useEffect(() => {
    if (showNewAnimation) {
      const timer = setTimeout(() => {
        setShowNewAnimation(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showNewAnimation]);
  
  const eventCardSize = useMemo(() => ({
    width: "200px",
    minHeight: "200px", // Minimum height to ensure card doesn't collapse
    height: "auto" // Allow height to flex to content
  }), []);

  const initialEffects = useMemo(() => {
    if (!Array.isArray(event.effects) || event.effects.length === 0) {
      return null;
    }
    
    // Group effects by priceChange value
    const grouped = {};
    event.effects.forEach((eff) => {
      const priceChange = eff.priceChange;
      if (!grouped[priceChange]) {
        grouped[priceChange] = [];
      }
      grouped[priceChange].push({
        sectorEmoji: getSectorEmoji(eff.indexName),
        indexName: toSentenceCase(eff.indexName)
      });
    });
    
    // Convert to array format: [{ priceChange: "+2", sectors: [{ emoji, name }, ...] }, ...]
    return Object.keys(grouped)
      .sort((a, b) => Number(b) - Number(a)) // Sort descending by value
      .map(priceChange => {
        const sign = Number(priceChange) >= 0 ? "+" : "";
        return {
          priceChange: `${sign}${priceChange}`,
          sectors: grouped[priceChange]
        };
      });
  }, [event.effects]);

  const conditionalInfo = useMemo(() => {
    if (!event.conditionalEffects || !Array.isArray(event.conditionalEffects.effects)) {
      return null;
    }
    
    // Group effects by priceChange value
    const grouped = {};
    event.conditionalEffects.effects.forEach((eff) => {
      const priceChange = eff.priceChange;
      if (!grouped[priceChange]) {
        grouped[priceChange] = [];
      }
      grouped[priceChange].push({
        sectorEmoji: getSectorEmoji(eff.indexName),
        indexName: toSentenceCase(eff.indexName)
      });
    });
    
    // Convert to array format: [{ priceChange: "+2", sectors: [{ emoji, name }, ...] }, ...]
    const condEffects = Object.keys(grouped)
      .sort((a, b) => Number(b) - Number(a)) // Sort descending by value
      .map(priceChange => {
        const sign = Number(priceChange) >= 0 ? "+" : "";
        return {
          priceChange: `${sign}${priceChange}`,
          sectors: grouped[priceChange]
        };
      });

    let probability = "";
    if (event.conditionalEffects.probability !== null && event.conditionalEffects.probability !== undefined) {
      probability = `${Math.round(event.conditionalEffects.probability * 100)}%`;
    } else if (event.conditionalEffects.dieRoll) {
      const dr = event.conditionalEffects.dieRoll;
      const total = dr.max - dr.min + 1;
      const successCount = dr.success.length;
      probability = `${Math.round((successCount / total) * 100)}%`;
    }

    const timingText = event.conditionalEffects.timing === "end" 
      ? "End of round" 
      : "Next round";
    
    return { timingText, probability, effects: condEffects };
  }, [event.conditionalEffects]);

  const isOngoingBubble = useMemo(() => 
    event.conditionalEffects && event.conditionalEffects.timing === "end",
    [event.conditionalEffects]
  );

  return (
    <div 
      className={`stock-card card ${showNewAnimation ? 'card-new' : ''} ${isOngoingBubble ? 'bubble-pulse' : ''}`}
      style={{
        ...eventCardSize,
        background: "linear-gradient(145deg, rgba(255, 250, 240, 0.98), rgba(255, 245, 230, 0.98)), linear-gradient(135deg, rgba(139, 0, 0, 0.18) 0%, rgba(100, 0, 0, 0.15) 100%)",
        border: "3px solid #8b0000",
        position: "relative",
        overflow: "hidden",
        boxShadow: "0 8px 20px rgba(139, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.7)",
        animation: isOngoingBubble 
          ? (showNewAnimation ? "cardNewAppear 1.5s ease-out, bubblePulse 1s 1.5s infinite" : "bubblePulse 1s infinite")
          : (showNewAnimation ? "cardNewAppear 1.5s ease-out, eventCardPulse 3s ease-in-out 1.5s infinite" : "eventCardPulse 3s ease-in-out infinite"),
        display: "flex",
        flexDirection: "column"
      }}
    >
      <style>
        {`
          @keyframes eventCardPulse {
            0%, 100% {
              box-shadow: 0 8px 20px rgba(139, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.7);
            }
            50% {
              box-shadow: 0 8px 24px rgba(139, 0, 0, 0.5), 0 2px 12px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.8);
            }
          }
          
          @keyframes bubblePulse {
            0%, 100% {
              transform: scale(1);
              box-shadow: 0 8px 20px rgba(139, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.7), 0 0 0 0 rgba(255, 193, 7, 0.7);
            }
            50% {
              transform: scale(1.02);
              box-shadow: 0 8px 20px rgba(139, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.7), 0 0 0 10px rgba(255, 193, 7, 0);
            }
          }
          
          @keyframes shimmer {
            0% {
              transform: translateX(-100%);
            }
            100% {
              transform: translateX(100%);
            }
          }
        `}
      </style>

      <div style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)",
        animation: "shimmer 4s ease-in-out infinite",
        pointerEvents: "none"
      }} />
      
      <div style={{
        position: "absolute",
        top: 0,
        right: 0,
        width: "60px",
        height: "60px",
        background: "linear-gradient(135deg, rgba(236, 72, 153, 0.2) 0%, rgba(219, 39, 119, 0.15) 100%)",
        clipPath: "polygon(100% 0, 0 0, 100% 100%)",
        pointerEvents: "none",
        boxShadow: "-2px 2px 8px rgba(219, 39, 119, 0.2)"
      }} />
      
      <div style={{
        position: "absolute",
        top: "8px",
        left: "8px",
        width: "4px",
        height: "4px",
        borderRadius: "50%",
        background: "rgba(219, 39, 119, 0.3)",
        boxShadow: "8px 0 0 rgba(219, 39, 119, 0.3), 16px 0 0 rgba(219, 39, 119, 0.3)",
        pointerEvents: "none"
      }} />
      
      <div className="stock-card-top" style={{
        background: "linear-gradient(135deg, rgba(236, 72, 153, 0.18), rgba(219, 39, 119, 0.12))",
        borderBottom: "2px solid rgba(219, 39, 119, 0.4)",
        position: "relative",
        paddingLeft: "8px",
        boxShadow: "0 2px 4px rgba(219, 39, 119, 0.1)"
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "6px"
        }}>
          <span style={{ 
            fontSize: "0.85rem",
            filter: "drop-shadow(0 1px 2px rgba(219, 39, 119, 0.3))"
          }}>📰</span>
          <span style={{ 
            flex: 1, 
            textAlign: "center",
            textShadow: "0 1px 2px rgba(255, 255, 255, 0.8)"
          }}>
            {event.displayValue || event.name || "Unknown Event"}
          </span>
        </div>
        <div style={{
          position: "absolute",
          bottom: "-2px",
          left: "10%",
          right: "10%",
          height: "2px",
          background: "linear-gradient(90deg, transparent, rgba(219, 39, 119, 0.4) 50%, transparent)"
        }} />
      </div>
      
      <div className="stock-card-spacer" style={{ 
        display: "flex", 
        flexDirection: "column",
        justifyContent: "flex-start",
        padding: "10px",
        gap: "10px",
        position: "relative",
        zIndex: 1,
        flex: "1 1 auto",
        minHeight: 0
      }}>
        {event.description && (
          <div style={{
            fontSize: "0.65rem",
            lineHeight: "1.35",
            color: "#444",
            textAlign: "center",
            fontStyle: "italic",
            padding: "8px 10px",
            background: "linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 250, 245, 0.8))",
            borderRadius: "6px",
            borderLeft: "3px solid rgba(219, 39, 119, 0.5)",
            borderRight: "3px solid rgba(219, 39, 119, 0.5)",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08), inset 0 1px 2px rgba(255, 255, 255, 0.5)"
          }}>
            <span style={{ 
              fontSize: "0.95em", 
              lineHeight: "0",
              color: "rgba(219, 39, 119, 0.6)"
            }}>"</span>
            {event.description}
            <span style={{ 
              fontSize: "0.95em", 
              lineHeight: "0",
              color: "rgba(219, 39, 119, 0.6)"
            }}>"</span>
          </div>
        )}
        
        <div style={{
          background: "rgba(255, 255, 255, 0.6)",
          borderRadius: "8px",
          padding: "6px",
          border: "1px dashed rgba(219, 39, 119, 0.3)",
          boxShadow: "0 2px 6px rgba(219, 39, 119, 0.1)"
        }}>
          <div style={{
            fontSize: "0.6rem",
            fontWeight: "700",
            color: "#db2777",
            textAlign: "center",
            textTransform: "uppercase",
            letterSpacing: "0.8px",
            marginBottom: "6px",
            background: "linear-gradient(90deg, rgba(236, 72, 153, 0.15), rgba(219, 39, 119, 0.2), rgba(236, 72, 153, 0.15))",
            padding: "3px 8px",
            borderRadius: "4px",
            display: "inline-block",
            width: "calc(100% - 16px)",
            margin: "0 0px 6px 0px",
            boxShadow: "0 1px 3px rgba(219, 39, 119, 0.2)"
          }}>
            ⚡ Market Impact ⚡
          </div>
          
          <div style={{
            fontSize: "0.7rem",
            fontWeight: "600",
            color: "#2d3748",
            textAlign: "left",
            padding: "8px",
            background: "linear-gradient(135deg, rgba(236, 72, 153, 0.15), rgba(219, 39, 119, 0.1))",
            borderRadius: "6px",
            border: "2px solid rgba(219, 39, 119, 0.3)",
            boxShadow: "0 2px 4px rgba(219, 39, 119, 0.12)"
          }}>
            {initialEffects ? (
              <div>
                {initialEffects.map((group, idx) => (
                  <div key={idx} style={{ marginBottom: idx < initialEffects.length - 1 ? '4px' : '0' }}>
                    {group.priceChange} {group.sectors.map((sector, sectorIdx) => (
                      <span key={sectorIdx}>
                        {sector.sectorEmoji} {sector.indexName}
                        {sectorIdx < group.sectors.length - 1 ? ', ' : ''}
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <div>📊 No effects</div>
            )}
            {isOngoingBubble && (
              <div style={{ 
                fontSize: '0.6rem', 
                opacity: 0.9, 
                marginTop: '4px',
                fontWeight: '600',
                color: "#db2777",
                textShadow: "0 1px 1px rgba(255, 255, 255, 0.5)"
              }}>
                ♻️ (ongoing each round)
              </div>
            )}
          </div>
        </div>
        
        {conditionalInfo && (
          <div style={{
            fontSize: "0.65rem",
            color: "#92400e",
            textAlign: "left",
            padding: "8px",
            background: "linear-gradient(135deg, rgba(251, 191, 36, 0.25), rgba(245, 158, 11, 0.2))",
            borderRadius: "6px",
            border: "2px solid rgba(245, 158, 11, 0.5)",
            fontWeight: "600",
            lineHeight: "1.3",
            boxShadow: "0 2px 6px rgba(245, 158, 11, 0.25), inset 0 1px 2px rgba(255, 255, 255, 0.3)"
          }}>
            <div>⚠️ {conditionalInfo.timingText}, {conditionalInfo.probability} bubble pop:</div>
            <div style={{ marginTop: '4px' }}>
              {conditionalInfo.effects.map((group, idx) => (
                <div key={idx} style={{ marginBottom: idx < conditionalInfo.effects.length - 1 ? '2px' : '0' }}>
                  {group.priceChange} {group.sectors.map((sector, sectorIdx) => (
                    <span key={sectorIdx}>
                      {sector.sectorEmoji} {sector.indexName}
                      {sectorIdx < group.sectors.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      <div style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: "4px",
        background: "linear-gradient(90deg, transparent, rgba(139, 0, 0, 0.7) 25%, rgba(139, 0, 0, 0.9) 50%, rgba(139, 0, 0, 0.7) 75%, transparent)",
        boxShadow: "0 -1px 3px rgba(139, 0, 0, 0.5)"
      }} />
    </div>
  );
};

export default EventCardView;